import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import Cookies from 'js-cookie';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Wrapper from '../layouts/Wrapper';
import { addToLocalBasket, addToUserBasket } from '../store/actions/basketActions';
import accordionBasket from '../assets/icons/accordionBasket.svg';
import { getService } from '../store/actions/getService';
import Spinner from '../components/Spinner';
import PageNotFound from './PageNotFound';
import Faq from '../components/home/Faq';
import {
  SITE_URL, getFAQSchema, getRouteBreadcrumb, getTestSchema, toPlainText,
} from '../utils/schema';

export const { REACT_APP_STORAGE_URL } = process.env;

function Service() {
  const dispatch = useDispatch();
  // eslint-disable-next-line no-unused-vars
  const navigate = useNavigate();
  const {
    isAuth,
    token,
  } = useSelector((state) => state.users);
  const {
    translations,
    loading: loadingTranslation,
  } = useSelector((state) => state.translation);
  const {
    loading,
    data,
  } = useSelector((state) => state.getService);
  const [lang, setLang] = useState(Cookies.get('i18next') || 'en');
  const { slug } = useParams();
  const [section, setSection] = useState('');
  useEffect(() => {
    dispatch(getService({
      slug,
      lang,
    }));
  }, []);
  useEffect(() => {
    const handleChange = () => setLang(Cookies.get('i18next') || 'en');
    window.addEventListener('languageChanged', handleChange);
    return () => window.removeEventListener('languageChanged', handleChange);
  }, []);
  useEffect(() => {
    if (Object.keys(data).length !== 0) {
      navigate(`/${lang}/section-description/${data.slug[lang]}`, { replace: true });
    }
  }, [lang, data]);
  useEffect(() => {
    setSection(data.type);
  }, [data]);
  const handleAddToBasket = async (item) => {
    if (!isAuth) {
      const basket = JSON.parse(localStorage.getItem('basket')) || [];
      const index = basket.findIndex((el) => el.id === item.id);
      if (index === -1) {
        localStorage.setItem('basket', JSON.stringify([...basket, item]));
        dispatch(addToLocalBasket({ ...item }));
        toast(translations.added[lang], { type: 'success' });
      } else {
        toast(translations.alreadyAdded[lang], { type: 'warning' });
      }
    } else {
      const { payload } = await dispatch(addToUserBasket({
        id: item.id,
        token,
        lang,
      }));
      if (Array.isArray(payload)) {
        toast(translations.added[lang]);
      } else {
        toast(translations.alreadyAdded[lang], { type: 'warning' });
      }
    }
  };
  if (Object.keys(data).length === 0 && !loading) {
    return (
      <PageNotFound />
    );
  }
  // Schema 4: MedicalTest, Schema 5: FAQPage — both fed by the rendered data.
  const testSchema = getTestSchema(data, lang);
  const faqSchema = getFAQSchema(data.faqs, lang);

  // Schema 6: BreadcrumbList — Home > section > test, the section being the
  // catalog page this test belongs to.
  const breadcrumbs = data.title && data.slug ? [
    getRouteBreadcrumb(data.type === 'laboratories' ? '/laboratory' : '/diagnostic-center', lang),
    {
      name: toPlainText(data.title[lang]),
      url: `${SITE_URL}/${lang}/section-description/${data.slug[lang]}`,
    },
  ].filter(Boolean) : [];

  return (
    <Wrapper breadcrumbs={breadcrumbs}>
      {(!loading && !loadingTranslation) ? (
        <>
          <Helmet>
            {testSchema && (
              <script type="application/ld+json">
                {JSON.stringify(testSchema)}
              </script>
            )}
            {faqSchema && (
              <script type="application/ld+json">
                {JSON.stringify(faqSchema)}
              </script>
            )}
          </Helmet>
          <div className="container small">
            <div className="service">
              <div className="service__right-side">
                <div className="service__title-box">
                  <div className="service__title">
                    <h1 className="service-title">{data.title[lang]}</h1>
                    {data[`pdf_${lang}`] ? (
                      <a
                        rel="noreferrer"
                        target="_blank"
                        href={`${REACT_APP_STORAGE_URL}/${data[`pdf_${lang}`]}`}
                        className="pdf-button"
                      >
                        <span className="pdf-icon">
                          <svg
                            width="18px"
                            height="18px"
                            viewBox="-4 0 40 40"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M25.6686 26.0962C25.1812 26.2401 24.4656 26.2563 23.6984 26.145C22.875 26.0256 22.0351 25.7739 21.2096 25.403C22.6817 25.1888 23.8237 25.2548 24.8005 25.6009C25.0319 25.6829 25.412 25.9021 25.6686 26.0962ZM17.4552 24.7459C17.3953 24.7622 17.3363 24.7776 17.2776 24.7939C16.8815 24.9017 16.4961 25.0069 16.1247 25.1005L15.6239 25.2275C14.6165 25.4824 13.5865 25.7428 12.5692 26.0529C12.9558 25.1206 13.315 24.178 13.6667 23.2564C13.9271 22.5742 14.193 21.8773 14.468 21.1894C14.6075 21.4198 14.7531 21.6503 14.9046 21.8814C15.5948 22.9326 16.4624 23.9045 17.4552 24.7459ZM14.8927 14.2326C14.958 15.383 14.7098 16.4897 14.3457 17.5514C13.8972 16.2386 13.6882 14.7889 14.2489 13.6185C14.3927 13.3185 14.5105 13.1581 14.5869 13.0744C14.7049 13.2566 14.8601 13.6642 14.8927 14.2326ZM9.63347 28.8054C9.38148 29.2562 9.12426 29.6782 8.86063 30.0767C8.22442 31.0355 7.18393 32.0621 6.64941 32.0621C6.59681 32.0621 6.53316 32.0536 6.44015 31.9554C6.38028 31.8926 6.37069 31.8476 6.37359 31.7862C6.39161 31.4337 6.85867 30.8059 7.53527 30.2238C8.14939 29.6957 8.84352 29.2262 9.63347 28.8054ZM27.3706 26.1461C27.2889 24.9719 25.3123 24.2186 25.2928 24.2116C24.5287 23.9407 23.6986 23.8091 22.7552 23.8091C21.7453 23.8091 20.6565 23.9552 19.2582 24.2819C18.014 23.3999 16.9392 22.2957 16.1362 21.0733C15.7816 20.5332 15.4628 19.9941 15.1849 19.4675C15.8633 17.8454 16.4742 16.1013 16.3632 14.1479C16.2737 12.5816 15.5674 11.5295 14.6069 11.5295C13.948 11.5295 13.3807 12.0175 12.9194 12.9813C12.0965 14.6987 12.3128 16.8962 13.562 19.5184C13.1121 20.5751 12.6941 21.6706 12.2895 22.7311C11.7861 24.0498 11.2674 25.4103 10.6828 26.7045C9.04334 27.3532 7.69648 28.1399 6.57402 29.1057C5.8387 29.7373 4.95223 30.7028 4.90163 31.7107C4.87693 32.1854 5.03969 32.6207 5.37044 32.9695C5.72183 33.3398 6.16329 33.5348 6.6487 33.5354C8.25189 33.5354 9.79489 31.3327 10.0876 30.8909C10.6767 30.0029 11.2281 29.0124 11.7684 27.8699C13.1292 27.3781 14.5794 27.011 15.985 26.6562L16.4884 26.5283C16.8668 26.4321 17.2601 26.3257 17.6635 26.2153C18.0904 26.0999 18.5296 25.9802 18.976 25.8665C20.4193 26.7844 21.9714 27.3831 23.4851 27.6028C24.7601 27.7883 25.8924 27.6807 26.6589 27.2811C27.3486 26.9219 27.3866 26.3676 27.3706 26.1461ZM30.4755 36.2428C30.4755 38.3932 28.5802 38.5258 28.1978 38.5301H3.74486C1.60224 38.5301 1.47322 36.6218 1.46913 36.2428L1.46884 3.75642C1.46884 1.6039 3.36763 1.4734 3.74457 1.46908H20.263L20.2718 1.4778V7.92396C20.2718 9.21763 21.0539 11.6669 24.0158 11.6669H30.4203L30.4753 11.7218L30.4755 36.2428ZM28.9572 10.1976H24.0169C21.8749 10.1976 21.7453 8.29969 21.7424 7.92417V2.95307L28.9572 10.1976ZM31.9447 36.2428V11.1157L21.7424 0.871022V0.823357H21.6936L20.8742 0H3.74491C2.44954 0 0 0.785336 0 3.75711V36.2435C0 37.5427 0.782956 40 3.74491 40H28.2001C29.4952 39.9997 31.9447 39.2143 31.9447 36.2428Z"
                              fill="#fff"
                            />
                          </svg>
                        </span>
                      </a>
                    ) : null}
                  </div>
                  {data.price[lang] && (
                  <div className="service__price-box">
                    <p
                      className="service-price"
                    >
                      {`${data.price[lang]} ${translations.amd[lang]}`}
                    </p>
                    {section === 'laboratories' && (
                    <button
                      className="service-price-btn"
                      type="button"
                      onClick={() => handleAddToBasket(data)}
                    >
                      <img src={accordionBasket} alt="" />
                      <p>{translations.add_to_cart[lang]}</p>
                    </button>
                    )}
                  </div>
                  )}
                </div>
                <div className="service-description">
                  <p
                    className="service-description-title"
                  >
                    {data.description_title && data.description_title[lang]}
                  </p>
                  <div
                    className="service-description-desc"
                    dangerouslySetInnerHTML={{ __html: data.description ? data.description[lang] : '' }}
                  />
                </div>
                {section === 'laboratories' && (
                <>
                  <div className="service-description">
                    <p
                      className="service-description-title"
                    >
                      {data.method_title && data.method_title[lang]}
                    </p>
                    <div className="service-description-desc">
                      <div
                        dangerouslySetInnerHTML={{ __html: data.method ? data.method[lang] : '' }}
                      />
                    </div>
                  </div>
                  <div className="service-description">
                    <p
                      className="service-description-title"
                    >
                      {data.biomass_title && data.biomass_title[lang]}
                    </p>
                    <div className="service-description-desc">
                      <div
                        dangerouslySetInnerHTML={{ __html: data.biomass ? data.biomass[lang] : '' }}
                      />
                    </div>
                  </div>
                </>
                )}
                <div className="service-description list">
                  <p
                    className="service-description-title"
                  >
                    {data.prerequisites_title && data.prerequisites_title[lang]}
                  </p>
                  <div className="service-description-desc">
                    <div
                      dangerouslySetInnerHTML={{ __html: data.prerequisites ? data.prerequisites[lang] : '' }}
                    />
                  </div>
                </div>
                {section === 'laboratories' && (
                <div className="service-description">
                  <p
                    className="service-description-title"
                  >
                    {data.implementation_schedule_title && data.implementation_schedule_title[lang]}
                  </p>
                  <div className="service-description-desc">
                    <div
                      dangerouslySetInnerHTML={{ __html: data.implementation_schedule ? data.implementation_schedule[lang] : '' }}
                    />
                  </div>
                </div>
                )}
                {data.price[lang] && section === 'laboratories' && (
                <button
                  className="service-price-btn bottom"
                  type="button"
                  onClick={() => handleAddToBasket(data)}
                >
                  <img src={accordionBasket} alt="" />
                  <p>{translations.add_to_cart[lang]}</p>
                </button>
                )}
              </div>
            </div>
          </div>
          {(data.faqs || []).length > 0 && (
          <Faq
            isService
            data={{
              data: data.faqs || [],
              title: {
                am: 'FAQ',
                en: 'FAQ',
                ru: 'FAQ',
              },
              content_title: {
                am: '',
                en: '',
                ru: '',
              },
            }}
          />
          )}
        </>
      ) : <Spinner width={100} height={100} />}
    </Wrapper>
  );
}

export default Service;
